'use client';

import { useTranslation } from '@/context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faBullseye, faCheck } from '@fortawesome/free-solid-svg-icons';

interface VisiMisiSectionClientProps {
    visiIdn?: string;
    visiEn?: string;
    misiIdn?: string;
    misiEn?: string;
}

export default function VisiMisiSectionClient({ visiIdn, visiEn, misiIdn, misiEn }: VisiMisiSectionClientProps) {
    const { t, language } = useTranslation();

    const currentVisi = language === 'id' ? visiIdn : visiEn;
    const currentMisi = language === 'id' ? misiIdn : misiEn;

    return (
        <section className="py-20 bg-unmulPale">
            <div className="container mx-auto px-6">
                <div className="text-center mb-16" data-aos="fade-up">
                    <span className="text-unmulYellow font-bold uppercase tracking-wider text-sm bg-unmulDarkGreen px-4 py-2 rounded-full">
                        {t('visionMission.label') || 'Visi & Misi'}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mt-6">
                        {t('visionMission.title') || 'Arah Menuju'} <span className="text-unmulGreen">{t('visionMission.subtitle') || 'Masa Depan'}</span>
                    </h2>
                </div>

                <div className="grid md:grid-cols-2 gap-10">
                    {/* VISI */}
                    <div className="bg-white rounded-3xl p-8 shadow-xl border-t-4 border-unmulYellow relative overflow-hidden" data-aos="fade-right">
                        <div className="absolute top-0 right-0 w-40 h-40 bg-unmulYellow/10 rounded-full -mr-20 -mt-20"></div>
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-unmulYellow rounded-2xl flex items-center justify-center mb-6">
                                <FontAwesomeIcon icon={faEye} className="text-unmulDarkGreen text-2xl" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">{t('visionMission.vision') || 'Visi'}</h3>
                            <div className="text-lg text-gray-600 leading-relaxed italic">
                                {currentVisi ? (
                                    <div dangerouslySetInnerHTML={{ __html: currentVisi }} className="[&>p]:mb-4" />
                                ) : (
                                    <p>
                                        &quot;Menjadi <strong className="text-unmulGreen">Universitas Berstandar Internasional</strong> yang Berperan dalam Pembangunan Bangsa Melalui Pendidikan, Penelitian, dan Pengabdian Kepada Masyarakat yang Bertumpu pada <strong className="text-unmulGreen">Sumber Daya Alam Tropis</strong>.&quot;
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* MISI */}
                    <div className="bg-white rounded-3xl p-8 shadow-xl border-t-4 border-unmulGreen relative overflow-hidden" data-aos="fade-left">
                        <div className="absolute top-0 right-0 w-40 h-40 bg-unmulGreen/10 rounded-full -mr-20 -mt-20"></div>
                        <div className="relative z-10">
                            <div className="w-16 h-16 bg-unmulGreen rounded-2xl flex items-center justify-center mb-6">
                                <FontAwesomeIcon icon={faBullseye} className="text-white text-2xl" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">{t('visionMission.mission') || 'Misi'}</h3>
                            <div className="text-gray-600">
                                {currentMisi ? (
                                    <div dangerouslySetInnerHTML={{ __html: currentMisi }} className="[&>p]:mb-2 [&>ul]:space-y-4 [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:gap-3 [&>ol]:list-decimal [&>ol]:ml-5 [&>ol]:space-y-2" />
                                ) : (
                                    <ul className="space-y-4 text-gray-600">
                                        <li className="flex items-start gap-3">
                                            <span className="w-6 h-6 bg-unmulGreen/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <FontAwesomeIcon icon={faCheck} className="text-unmulGreen text-xs" />
                                            </span>
                                            <span>Menyelenggarakan pendidikan bermutu tinggi dalam menghasilkan sumber daya manusia yang kompetitif</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <span className="w-6 h-6 bg-unmulGreen/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <FontAwesomeIcon icon={faCheck} className="text-unmulGreen text-xs" />
                                            </span>
                                            <span>Mengembangkan penelitian inovatif berbasis sumber daya alam tropis</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <span className="w-6 h-6 bg-unmulGreen/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <FontAwesomeIcon icon={faCheck} className="text-unmulGreen text-xs" />
                                            </span>
                                            <span>Melaksanakan pengabdian kepada masyarakat yang bermanfaat bagi kesejahteraan bangsa</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <span className="w-6 h-6 bg-unmulGreen/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                                <FontAwesomeIcon icon={faCheck} className="text-unmulGreen text-xs" />
                                            </span>
                                            <span>Membangun tata kelola universitas yang transparan, akuntabel, dan profesional</span>
                                        </li>
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
